import React, { useState, useEffect, useRef } from "react";
import { LiaCookieBiteSolid } from "react-icons/lia";
import Cookies from "js-cookie";
import { gsap } from "gsap";
import { useTranslation } from "react-i18next";

const CookieBanner = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [cookieAnalytic, setCookieAnalytic] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);

  const bannerRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const storedSettings = Cookies.get("matriSettings");
    if (storedSettings) {
      const parsed = JSON.parse(storedSettings);
      setCookieAnalytic(parsed.analytics);
      setBannerOpen(false);
    } else {
      setBannerOpen(true);
      document.body.classList.add("cookie-open");
    }
  }, []);

  useEffect(() => {
    if (bannerOpen) {
      document.body.classList.add("cookie-open");
    } else {
      document.body.classList.remove("cookie-open");
    }
  }, [bannerOpen]);

  useEffect(() => {
    if (bannerRef.current) {
      if (bannerOpen) {
        gsap.fromTo(
          bannerRef.current,
          { y: 50, opacity: 0, scale: 0.95 },
          { y: 0, opacity: 1, scale: 1, duration: 0.25, ease: "power2.out" }
        );
      } else {
        gsap.to(bannerRef.current, {
          y: 50,
          opacity: 0,
          scale: 0.95,
          duration: 0.25,
          ease: "power2.in"
        });
      }
    }
  }, [bannerOpen]);

  const saveConsent = (analytics) => {
    Cookies.set("matriSettings", JSON.stringify({ analytics }), { expires: 365 });
    setCookieAnalytic(analytics);
    setBannerOpen(false);
    setShowDetails(false);
  };

  return (
    <>
      {!bannerOpen && (
        <div className="fixed bottom-4 right-4 z-50">
          <button
            className="bg-rose-200/60 p-3 rounded-full shadow-lg hover:bg-rose-300 transition"
            onClick={() => setBannerOpen(true)}
            aria-label={t("cookie.reopen")}
          >
            <LiaCookieBiteSolid className="h-6 w-6 text-rose-700" />
          </button>
        </div>
      )}

      {bannerOpen && (
        <div
          ref={bannerRef}
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm p-4"
        >
          <div className="bg-white/90 backdrop-blur-md rounded-xl shadow-xl max-w-md w-full p-6 flex flex-col gap-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center">
              {t("cookie.title")}
            </h2>
            <p className="text-slate-600 text-center">{t("cookie.message")}</p>

            <button
              className="text-rose-700 font-semibold text-sm self-center hover:underline"
              onClick={() => setShowDetails((prev) => !prev)}
            >
              {showDetails ? t("cookie.toggle.hide") : t("cookie.toggle.show")}
            </button>

            {showDetails && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between bg-slate-100 rounded-lg p-3">
                  <span>{t("cookie.labels.necessary")}</span>
                  <div className="bg-slate-300 w-12 h-6 rounded-full relative">
                    <span className="absolute left-0 top-0 w-6 h-6 rounded-full bg-rose-500 shadow-md"></span>
                  </div>
                </div>

                <div className="flex items-center justify-between bg-slate-100 rounded-lg p-3">
                  <span>{t("cookie.labels.analytics")}</span>
                  <button
                    onClick={() => setCookieAnalytic(!cookieAnalytic)}
                    className={`w-12 h-6 rounded-full relative transition-all ${
                      cookieAnalytic ? "bg-rose-500" : "bg-slate-300"
                    }`}
                    aria-pressed={cookieAnalytic}
                  >
                    <span
                      className={`absolute top-0 left-0 w-6 h-6 rounded-full bg-white shadow-md transform transition-transform ${
                        cookieAnalytic ? "translate-x-6" : ""
                      }`}
                    ></span>
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between mt-4">
              <button
                className="bg-slate-700 text-white px-4 py-2 rounded hover:bg-slate-800 transition"
                onClick={() => saveConsent(false)}
              >
                {t("cookie.actions.reject")}
              </button>
              <button
                className="bg-rose-600 text-white px-4 py-2 rounded hover:bg-rose-700 transition"
                onClick={() => saveConsent(true)}
              >
                {t("cookie.actions.accept")}
              </button>
            </div>

            {showDetails && (
              <button
                className="mt-2 bg-rose-200/70 text-rose-700 px-4 py-2 rounded self-center hover:bg-rose-300 transition"
                onClick={() => saveConsent(cookieAnalytic)}
              >
                {t("cookie.actions.acceptSelected")}
              </button>
            )}
          </div>
        </div>
      )}

      <style>{`
        body.cookie-open {
          overflow: hidden;
          touch-action: none;
        }
      `}</style>
    </>
  );
};

export default CookieBanner;
